import { Box, Flex } from "grid-styled";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { themeGet } from "styled-system";

import { ActionButtonContainer } from "./components";
import useAdvancedReportPage from "./hooks/useAdvancedReportPage";
import useQueryVariables from "./hooks/useQueryVariables";

import { BucketContainer } from "pstat-anywhere/components/document_reports/ReportBuckets";
import {
  ReportHeader,
  ReportPage
} from "pstat-anywhere/components/pages/reports";
import Export from "pstat-anywhere/components/partials/export/Export";
import { HeaderLink } from "pstat-anywhere/components/partials/HeaderLinks";
import { StyledFontAwesomeIcon } from "pstat-anywhere/components/partials/icons";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import Card from "pstat-design-system/Card";
import { InputFormElement } from "pstat-design-system/inputs/FormElements";
import LoadingSpinner from "pstat-design-system/LoadingSpinner";
import { Button } from "pstat-design-system/shared/Button";
import {
  HorizontalScrollTable,
  SortablePolicyTable
} from "pstat-design-system/tables";
import { Tab, TabContainer } from "pstat-design-system/Tabs";
import { Text } from "pstat-design-system/text/paragraph";


const StyledRssFeedContainer = styled.div`
  width: 100%;
  text-align: right;
  padding-bottom: ${themeGet("space.2")}px;
`;

export const DefaultActionPanel = ({
  selectedPolicies,
  onActionButtonClick,
  actionTextKey,
  loading
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  function handleCommentChange(event) {
    setComment(event.target.value);
  }
  function handleActionButtonClick(event) {
    onActionButtonClick({ selectedPolicies, comment });
  }
  return (
    <Fragment>
      <InputFormElement
        value={comment}
        placeholder={t("documentControl.view.commentForm.commentPanel.header")}
        name="report-action-comment"
        onChange={handleCommentChange}
        ContainerProps={{ mb: 0, display: "flex" }}
      />
      <Button
        buttonStyle="primary"
        onClick={handleActionButtonClick}
        ml={2}
        processing={loading}
      >
        {t(actionTextKey)}
      </Button>
    </Fragment>
  );
};

function ReportContent({
  policies,
  sort,
  onSortChange,
  labels: { documentLabelPlural, documentLabel },
  totalCount,
  currentPageResultsCount,
  currentRouteName,
  onTabChange,
  tableType,
  onActionClick,
  onSelectAllCheckboxClick,
  selectAllCheckboxState,
  selectedPolicies,
  showActionButtonPanel,
  singularSelectedActionsLabel,
  pluralSelectedActionsLabel,
  showTabsPanel,
  tabs,
  noContentMessage,
  noSort,
  hitsPerPage,
  TableComponent = SortablePolicyTable,
  renderActionPanel,
  renderFilterPanel,
  rowKey,
  alwaysShowActionPanel,
  customOnRow,
  otherTableProps
}) {
  const { t } = useTranslation();
  let selectedActionsLabel = null;
  if (selectedPolicies.length > 1) {
    selectedActionsLabel = pluralSelectedActionsLabel
      ? pluralSelectedActionsLabel
      : documentLabelPlural.toLowerCase();
  } else {
    selectedActionsLabel = singularSelectedActionsLabel
      ? singularSelectedActionsLabel
      : documentLabel.toLowerCase();
  }
  return (
    <Fragment>
      {showTabsPanel && !showActionButtonPanel && (
        <TabContainer
          selectedTabId={currentRouteName}
          onSelectedTabChange={onTabChange}
          totalCount={totalCount}
          currentPageResultsCount={currentPageResultsCount}
        >
          {tabs.map(({ id, textKey }) => (
            <Tab id={id} key={id}>
              <Text color="nav.25">{t(textKey)}</Text>
            </Tab>
          ))}
        </TabContainer>
      )}
      {(alwaysShowActionPanel || (showActionButtonPanel && !showTabsPanel)) && (
        <ActionButtonContainer>
          {selectedPolicies.length > 0 && (
            <Flex alignItems="center" height="100%">
              <Text color="nav.100">
                {t(
                  "documentReports.advancedReportPage.action.selectedPoliciesCount",
                  {
                    count: selectedPolicies.length,
                    label: selectedActionsLabel
                  }
                )}
              </Text>
            </Flex>
          )}
          <Flex alignItems="center" height="100%" justify="flex-end" flex="1">
            {renderActionPanel()}
          </Flex>
        </ActionButtonContainer>
      )}
      <Card>
        {renderFilterPanel()}
        {policies ? (
          <Fragment>
            {policies.length ? (
              <Fragment>
                <TableComponent
                  tableType={tableType}
                  policies={policies}
                  numberToDisplay={hitsPerPage}
                  borderRadius="0"
                  sort={sort}
                  onSortChange={onSortChange}
                  onActionClick={onActionClick}
                  selectedCheckboxPks={selectedPolicies}
                  onSelectAllCheckboxClick={onSelectAllCheckboxClick}
                  selectAllCheckboxState={selectAllCheckboxState}
                  noSort={noSort}
                  rowKey={rowKey}
                  customOnRow={customOnRow}
                  {...otherTableProps}
                />
              </Fragment>
            ) : (
              <Text>{noContentMessage}</Text>
            )}
          </Fragment>
        ) : (
          <Flex justify="center" align="center" mt={10}>
            <Box>
              <LoadingSpinner size={100} />
            </Box>
          </Flex>
        )}
      </Card>
    </Fragment>
  );
}

function AdvancedReportPage({
  headerText,
  csvQueryArgs,
  route,
  router,
  location,
  labels,
  CSVQuery,
  csvQueryName,
  tabs,
  data,
  showActionButtonPanel,
  showTabsPanel,
  tableType,
  noContentMessage,
  noSort,
  frozenColumnTable = false,
  alwaysShowActionPanel = false,
  renderActionPanel,
  singularSelectedActionsLabel,
  pluralSelectedActionsLabel,
  renderFilterPanel = () => null,
  rowKey,
  showPagination = true,
  customOnRow,
  renderHeader,
  BreadcrumbComponent,
  otherTableProps,
  initiallySelectedPks = [],
  BucketsComponent,
  isCheckboxDisabled,
  headerTooltip,
  rssFeedUrl
}) {
  const policies = showPagination ? data?.edges?.map(edge => edge.node) : data;
  const { variables, hitsPerPage } = useQueryVariables({ location });
  const {
    currentRouteName,
    selectedPolicies,
    updatedPolicies,
    selectAllCheckboxState,
    handleTabChange,
    handleSortChange,
    handleCheckboxClick,
    handleClearSelection,
    handleSelectAll,
    handleSelectAllCheckboxClick
  } = useAdvancedReportPage({
    route,
    router,
    location,
    tabs,
    policies,
    initiallySelectedPks,
    isCheckboxDisabled
  });
  const { t } = useTranslation();
  const hasSelectedPolicies = selectedPolicies.length > 0;
  return (
    <ReportPage
      hitsPerPage={hitsPerPage}
      totalNumberOfResults={data?.totalCount}
      showPagination={showPagination}
    >
      <ReportHeader
        title={headerText}
        BreadcrumbComponent={BreadcrumbComponent}
        tooltip={headerTooltip}
      >
        {rssFeedUrl && (
          <HeaderLink
            href={rssFeedUrl}
            linkIcon={["fa", "rss"]}
            linkLabel={t("documentControl.view.share.modal.rssFeed.title")}
          />
        )}
        {CSVQuery && (
          <Export
            csvQuery={CSVQuery}
            csvQueryArgs={csvQueryArgs}
            queryName={csvQueryName}
          />
        )}
        {renderHeader && <Fragment>{renderHeader()}</Fragment>}
      </ReportHeader>
      {BucketsComponent && (
        <BucketContainer id="buckets-container">
          <BucketsComponent />
        </BucketContainer>
      )}
      <ReportContent
        policies={updatedPolicies}
        labels={labels}
        sort={variables.sort}
        onSortChange={handleSortChange}
        onTabChange={handleTabChange}
        currentRouteName={currentRouteName}
        totalCount={showPagination ? data?.totalCount : policies?.length}
        currentPageResultsCount={
          showPagination ? data?.edges?.length : policies?.length
        }
        onActionClick={handleCheckboxClick}
        onSelectAllCheckboxClick={handleSelectAllCheckboxClick}
        selectAllCheckboxState={selectAllCheckboxState}
        tabs={tabs}
        showActionButtonPanel={showActionButtonPanel && hasSelectedPolicies}
        showTabsPanel={showTabsPanel && !hasSelectedPolicies}
        tableType={tableType}
        noContentMessage={noContentMessage}
        noSort={noSort}
        selectedPolicies={selectedPolicies}
        hitsPerPage={hitsPerPage}
        TableComponent={
          frozenColumnTable ? HorizontalScrollTable : SortablePolicyTable
        }
        alwaysShowActionPanel={alwaysShowActionPanel}
        singularSelectedActionsLabel={singularSelectedActionsLabel}
        pluralSelectedActionsLabel={pluralSelectedActionsLabel}
        renderActionPanel={() =>
          renderActionPanel(selectedPolicies, {
            handleClearSelection,
            handleSelectAll
          })
        }
        renderFilterPanel={renderFilterPanel}
        renderHeader={renderHeader}
        rowKey={rowKey}
        showPagination={showPagination}
        customOnRow={customOnRow}
        otherTableProps={otherTableProps}
      />
    </ReportPage>
  );
}

export default withLabelContext(AdvancedReportPage);
